'use client';

import { useEffect, useMemo, useState } from 'react';
import { HospitalityGapError, useHospitalitySource } from '@/src/lib/hospitality/hospitalitySource';
import { useRestaurantSite } from '@/src/lib/hospitality/useRestaurantSite';
import type {
  AllergenDeclaration,
  Menu,
  MenuItem,
  MenuModifierGroup,
  Orderability,
  RestaurantSite,
} from '@/src/lib/hospitality/wire';
import { formatMoney } from '@/src/lib/money';
import {
  BlockSection,
  EmptyNote,
  SiteStateNotice,
  useActiveLocale,
  useHospitalityMessages,
} from './shared';
import { MenuJsonLd } from './menuJsonLd';
import {
  allergenSummary,
  allergenTable,
  effectiveOrderability,
  serviceOptions,
  timeInZone,
  unorderableReason,
  type ServiceOption,
} from './menuModel';

/**
 * The menu a diner reads: sections in the operator's order, every dish priced,
 * every allergen said out loud, and the ones the kitchen will not cook right
 * now greyed with the reason rather than removed.
 *
 * <h3>Three rules this block exists to keep</h3>
 *
 * <p><strong>An unorderable item is never hidden.</strong> A menu that drops
 * its evening dishes at lunchtime reads as a shorter menu, and a diner who saw
 * it yesterday thinks the restaurant has changed. Off items render at reduced
 * emphasis with a sentence saying why and, when the server knows, when it comes
 * back.
 *
 * <p><strong>An allergen the roll-up could not determine reads "not declared —
 * please ask".</strong> Never "free from", never nothing. `AllergenLevel.UNKNOWN`
 * is a declared state — "nobody has said" — and the server ranks it above
 * `FREE_FROM` for the same reason. This is the one failure in the vertical that
 * puts somebody in hospital.
 *
 * <p><strong>Availability comes from the server.</strong> The channel's
 * timezone, its windows and its 86 list all live there; nothing here compares a
 * clock to a window. The service picker below sends a different INSTANT and
 * reads the answer back — it never works one out.
 *
 * <h3>Where the data comes from</h3>
 *
 * <p>`HospitalitySource.menu(at)` and `.orderable(productId, at)`, and no
 * `fetch` — those go through this app's `/api/storefront/hospitality/menu**`
 * handlers, which resolve the domain from the request's own host. The block
 * names no tenant and no channel; it asks for "the menu of the restaurant whose
 * page this is". Menu bodies are cached for a minute; each dish's orderability
 * is re-read uncached, which is what makes an 86'd dish grey out inside a
 * service rather than at the top of the next minute.
 */
export interface RestaurantMenuProps {
  heading?: string;
  /**
   * ISO instant to resolve the menu for. Unset means "now", which is what a
   * diner arriving on the page wants. An operator may pin one — a page called
   * "Sunday lunch" is a legitimate thing to author — and pinning it also turns
   * the service picker off, because the page has already chosen.
   */
  at?: string;
  /** Show the allergen badges under each item. */
  showAllergens?: boolean;
  /** Offer the "showing the menu for…" picker built from the site's hours. */
  showServicePicker?: boolean;
  /**
   * The order page the operator authored, if they have one. No default: this
   * app invents no URLs, and a dead "Add to order" link is worse than none.
   * `?add=<productId>` is appended, which is the contract 11.10b reads.
   */
  orderPageHref?: string;
  /**
   * Re-read each dish's orderability uncached after the menu body arrives.
   * Bounded by {@link liveAvailabilityLimit} so a hundred-dish menu does not
   * open a hundred connections.
   */
  liveAvailability?: boolean;
  liveAvailabilityLimit?: number;
}

type MenuState =
  | { status: 'loading' }
  | { status: 'ready'; menu: Menu | null }
  | { status: 'gap' }
  | { status: 'error' };

export function RestaurantMenu({
  heading,
  at,
  showAllergens = true,
  showServicePicker = true,
  orderPageHref,
  liveAvailability = true,
  liveAvailabilityLimit = 40,
}: RestaurantMenuProps) {
  const t = useHospitalityMessages();
  const locale = useActiveLocale();
  const source = useHospitalitySource();
  const state = useRestaurantSite();

  // The instant the diner is previewing. `null` is "now", which is NOT the same
  // as an instant captured when the page loaded: a page left open over a
  // service change should ask again for now, not for the now it opened at.
  const [previewAt, setPreviewAt] = useState<string | null>(null);
  const askedAt = at ?? previewAt ?? undefined;

  const [menuState, setMenuState] = useState<MenuState>({ status: 'loading' });
  const [live, setLive] = useState<Record<number, Orderability>>({});

  const ready = state.status === 'ready';
  const site = ready ? state.site : null;

  useEffect(() => {
    if (!source || !ready) return;
    let cancelled = false;
    setMenuState({ status: 'loading' });
    setLive({});
    source
      .menu(askedAt)
      .then((loaded) => {
        if (!cancelled) setMenuState({ status: 'ready', menu: loaded });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // A named gap and a broken backend are different sentences to a diner
        // and different jobs for whoever has to fix them. Loud in the console
        // either way: the operator needs to know which read failed.
        setMenuState({ status: error instanceof HospitalityGapError ? 'gap' : 'error' });
        console.error('[RestaurantMenu]', error);
      });
    return () => {
      cancelled = true;
    };
  }, [source, ready, askedAt]);

  const menu = menuState.status === 'ready' ? menuState.menu : null;

  // The dishes worth re-asking about: the ones the (possibly a minute old) menu
  // body says are ON. Those are the ones a chef can take away mid-service, and
  // the direction that matters — an order the kitchen cannot cook. Skipped
  // entirely while previewing another service, where "right now" is not the
  // question being asked.
  const liveTargets = useMemo(() => {
    if (!liveAvailability || askedAt || !menu) return [];
    const ids: number[] = [];
    for (const section of menu.sections) {
      for (const item of section.items) {
        if (item.orderable && ids.length < liveAvailabilityLimit) ids.push(item.productId);
      }
    }
    return ids;
  }, [liveAvailability, askedAt, menu, liveAvailabilityLimit]);

  useEffect(() => {
    if (!source || liveTargets.length === 0) return;
    let cancelled = false;
    void (async () => {
      for (const productId of liveTargets) {
        if (cancelled) return;
        try {
          const answer = await source.orderable(productId);
          if (cancelled || !answer) continue;
          setLive((current) => ({ ...current, [productId]: answer }));
        } catch (error: unknown) {
          // One dish failing to answer leaves the menu body's verdict standing.
          // It does NOT turn the dish off: the body is a real answer from the
          // server, just an older one.
          console.error('[RestaurantMenu.orderable]', productId, error);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source, liveTargets]);

  const services = useMemo(
    () => (site && showServicePicker && !at ? serviceOptions(site) : []),
    [site, showServicePicker, at],
  );

  const title = heading ?? t('hospitality.menu.heading');

  if (!ready || !site) {
    return (
      <BlockSection heading={title}>
        <SiteStateNotice state={state} t={t} />
      </BlockSection>
    );
  }

  if (menuState.status === 'loading') {
    return (
      <BlockSection heading={title}>
        <EmptyNote>{t('hospitality.loading')}</EmptyNote>
      </BlockSection>
    );
  }

  if (menuState.status === 'gap') {
    return (
      <BlockSection heading={title}>
        <EmptyNote>{t('hospitality.menu.empty')}</EmptyNote>
      </BlockSection>
    );
  }

  if (menuState.status === 'error') {
    return (
      <BlockSection heading={title}>
        <EmptyNote>{t('hospitality.menu.error')}</EmptyNote>
      </BlockSection>
    );
  }

  if (!menu || menu.sections.length === 0) {
    return (
      <BlockSection heading={title}>
        <EmptyNote>{t('hospitality.menu.empty')}</EmptyNote>
      </BlockSection>
    );
  }

  const currency = menu.currency || site.currency;

  return (
    <BlockSection heading={title}>
      <MenuJsonLd site={site} menu={menu} />

      {services.length > 0 ? (
        <ServicePicker
          services={services}
          value={previewAt}
          onChange={setPreviewAt}
          asOf={timeInZone(menu.asOf, site.timeZone, locale)}
          t={t}
        />
      ) : null}

      <div className="space-y-10">
        {menu.sections.map((section) => (
          <section key={`${section.categoryId ?? section.name}-${section.position}`}>
            <h3 className="text-xl font-semibold">{section.name}</h3>
            {section.items.length === 0 ? (
              <EmptyNote>{t('hospitality.menu.sectionEmpty')}</EmptyNote>
            ) : (
              <ul className="mt-4 space-y-6">
                {section.items.map((item) => (
                  <MenuItemRow
                    key={item.productId}
                    item={item}
                    live={live[item.productId]}
                    site={site}
                    currency={currency}
                    locale={locale}
                    showAllergens={showAllergens}
                    orderPageHref={orderPageHref}
                    t={t}
                  />
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
    </BlockSection>
  );
}

type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * "Showing the menu for now", with the day's other services beside it.
 *
 * <p>Each option is an instant handed straight back to `menu(at)`. The options
 * are the operator's own opening rows, not a list of meal names — "lunch",
 * "öğle" and "brunch" are the operator's business, and half of them do not
 * exist at any given restaurant.
 */
function ServicePicker({
  services,
  value,
  onChange,
  asOf,
  t,
}: {
  services: ServiceOption[];
  value: string | null;
  onChange: (next: string | null) => void;
  asOf: string | null;
  t: Translate;
}) {
  return (
    <p className="mb-8 flex flex-wrap items-center gap-2 text-sm">
      <label htmlFor="hospitality-service" className="text-[var(--color-text-secondary)]">
        {t('hospitality.menu.service.label')}
      </label>
      <select
        id="hospitality-service"
        className="rounded-md border border-[var(--color-border)] px-2 py-1"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value || null)}
      >
        <option value="">{t('hospitality.menu.service.now')}</option>
        {services.map((service) => (
          <option key={service.value} value={service.value}>
            {service.closesAt
              ? t('hospitality.menu.service.window', {
                  opens: service.opensAt ?? '',
                  closes: service.closesAt,
                })
              : (service.opensAt ?? '')}
          </option>
        ))}
      </select>
      {asOf ? (
        <span className="text-[var(--color-text-secondary)]">
          {t('hospitality.menu.asOf', { time: asOf })}
        </span>
      ) : null}
    </p>
  );
}

/**
 * One dish.
 *
 * <p>An unorderable dish is dimmed AND carries its reason in words: colour is
 * never the only carrier of a state a diner has to act on (WCAG 2.2 AA 1.4.1),
 * and "greyed out" means nothing to a screen reader.
 */
function MenuItemRow({
  item,
  live,
  site,
  currency,
  locale,
  showAllergens,
  orderPageHref,
  t,
}: {
  item: MenuItem;
  live?: Orderability;
  site: RestaurantSite;
  currency: string;
  locale: string;
  showAllergens: boolean;
  orderPageHref?: string;
  t: Translate;
}) {
  const verdict = effectiveOrderability(item, live);
  const reason = verdict.orderable
    ? null
    : unorderableReason(verdict.code, verdict.nextAvailableAt, site.timeZone, locale);
  const price = formatMoney({ amount: item.price ?? '', currency }, locale);
  const summary = allergenSummary(item.allergens);

  return (
    <li className={`flex gap-4 ${verdict.orderable ? '' : 'opacity-60'}`}>
      {item.mediaUrl ? (
        // eslint-disable-next-line @next/next/no-img-element -- the asset host is
        // per-tenant and not known at build time, so next/image's remote allowlist
        // cannot be configured for it. The row is already restricted server-side
        // to an image with its metadata stripped.
        <img
          src={item.mediaUrl}
          // An empty alt is the operator saying "decorative"; the title supplies
          // the accessible name either way.
          alt={item.mediaAlt}
          className="h-24 w-24 shrink-0 rounded-md object-cover"
          loading="lazy"
          decoding="async"
        />
      ) : null}

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4">
          <h4 className="text-base font-medium">{item.title}</h4>
          {price ? (
            <p className="text-base font-medium">
              {price}{' '}
              <span className="text-xs font-normal text-[var(--color-text-secondary)]">
                {t(
                  item.taxInclusive
                    ? 'hospitality.menu.taxIncluded'
                    : 'hospitality.menu.taxExcluded',
                )}
              </span>
            </p>
          ) : null}
        </div>

        {item.description ? (
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{item.description}</p>
        ) : null}

        {reason ? (
          <p className="mt-1 text-sm font-medium">{t(reason.key, reason.params)}</p>
        ) : null}

        {showAllergens ? <AllergenBadges summary={summary} t={t} /> : null}

        <details className="mt-2">
          <summary className="cursor-pointer text-sm underline">
            {t('hospitality.menu.details')}
          </summary>
          <AllergenTable declarations={item.allergens} t={t} />
          <ModifierGroups groups={item.modifierGroups} currency={currency} locale={locale} t={t} />
          {orderPageHref && verdict.orderable ? (
            <p className="mt-3">
              <a className="text-sm underline" href={addToOrderHref(orderPageHref, item.productId)}>
                {t('hospitality.menu.addToOrder')}
              </a>
            </p>
          ) : null}
        </details>
      </div>
    </li>
  );
}

/** `?add=<productId>` on the operator's own order page, keeping any query it
 *  already carries. Parsed rather than concatenated so a href that already has
 *  a `?` does not end up with two. */
export function addToOrderHref(orderPageHref: string, productId: number): string {
  const marker = orderPageHref.includes('?') ? '&' : '?';
  return `${orderPageHref}${marker}add=${encodeURIComponent(String(productId))}`;
}

/**
 * Contains, may contain, and not declared.
 *
 * <p>`FREE_FROM` gets no badge — a row of "free from mustard" on every dish is
 * noise, and the diner who needs the detail opens the full table. The other
 * three are all said, `UNKNOWN` loudest: it is the one a diner must not read as
 * an absence.
 */
function AllergenBadges({ summary, t }: { summary: ReturnType<typeof allergenSummary>; t: Translate }) {
  const names = (rows: AllergenDeclaration[]) =>
    rows.map((row) => allergenLabel(row, t)).join(', ');
  if (
    summary.contains.length === 0 &&
    summary.mayContain.length === 0 &&
    summary.undeclared.length === 0
  ) {
    return null;
  }
  return (
    <ul className="mt-2 flex flex-wrap gap-2 text-xs">
      {summary.contains.length > 0 ? (
        <li className="rounded-full border border-[var(--color-border)] px-2 py-0.5">
          {t('hospitality.allergen.contains', { list: names(summary.contains) })}
        </li>
      ) : null}
      {summary.mayContain.length > 0 ? (
        <li className="rounded-full border border-[var(--color-border)] px-2 py-0.5">
          {t('hospitality.allergen.mayContain', { list: names(summary.mayContain) })}
        </li>
      ) : null}
      {summary.undeclared.length > 0 ? (
        <li className="rounded-full border border-[var(--color-border)] px-2 py-0.5 font-medium">
          {t('hospitality.allergen.undeclared', { list: names(summary.undeclared) })}
        </li>
      ) : null}
    </ul>
  );
}

/** All fourteen, always, with the level spelled out in words. */
function AllergenTable({
  declarations,
  t,
}: {
  declarations: AllergenDeclaration[];
  t: Translate;
}) {
  const rows = allergenTable(declarations);
  return (
    <table className="mt-3 w-full text-left text-sm">
      <caption className="text-left text-xs text-[var(--color-text-secondary)]">
        {t('hospitality.allergen.caption')}
      </caption>
      <thead>
        <tr>
          <th scope="col" className="py-1 font-medium">
            {t('hospitality.allergen.columnAllergen')}
          </th>
          <th scope="col" className="py-1 font-medium">
            {t('hospitality.allergen.columnLevel')}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.allergenKey}>
            <th scope="row" className="py-1 font-normal">
              {allergenLabel(row, t)}
            </th>
            <td className="py-1">{t(`hospitality.allergen.level.${row.level}`)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The tenant's own caption when it sent one, this app's when it did not, and
 *  the raw key as the last resort — never a blank cell. */
function allergenLabel(row: AllergenDeclaration, t: Translate): string {
  if (row.label) return row.label;
  const translated = t(`hospitality.allergen.name.${row.allergenKey}`);
  return translated === `hospitality.allergen.name.${row.allergenKey}`
    ? row.allergenKey
    : translated;
}

/** The kit slots, read-only. Choosing between them is 11.10b's job; showing a
 *  diner that a burger comes with a choice of side is this one's. */
function ModifierGroups({
  groups,
  currency,
  locale,
  t,
}: {
  groups: MenuModifierGroup[];
  currency: string;
  locale: string;
  t: Translate;
}) {
  if (groups.length === 0) return null;
  return (
    <div className="mt-3 space-y-2">
      {groups.map((group) => (
        <div key={group.kitComponentId}>
          <p className="text-sm font-medium">
            {group.label}
            {group.required ? ` — ${t('hospitality.menu.modifier.required')}` : ''}
          </p>
          <ul className="text-sm text-[var(--color-text-secondary)]">
            {group.options.map((option) => {
              const extra = formatMoney({ amount: option.price ?? '', currency }, locale);
              return (
                <li key={option.productId} className={option.orderable ? '' : 'opacity-60'}>
                  {option.label}
                  {extra ? ` (+${extra})` : ''}
                  {option.orderable ? '' : ` — ${t('hospitality.menu.reason.soldOut')}`}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
